import React, { useContext, useEffect, useState } from 'react';
import { CarrierContactPersonDTO, CarrierSearchOutDTO } from '@api/ringil-api.v2';
import { useImmer } from 'use-immer';
import { isNullOrUndefined } from '@utils/utils';
import * as yup from 'yup';
import { findValidationMessage } from '@utils/validation';
import useValidation from '@hooks/validation-hook/useValidation';
import { ApiContext } from '@api/api';
import { useTranslation } from 'react-i18next';
import useBEValidation from '@hooks/useBEValidation/useBEValidation.tsx';
import InputLayout from '@components/obsolete/Form/InputLayout/InputLayout.tsx';
import InputWrapper from '@components/obsolete/Form/InputWrapper/InputWrapper.tsx';
import InputCountry from '@components/obsolete/Form/InputCountry/InputCountry.tsx';
import { getCrnFormatDescription, validateCrn } from '@utils/crnValidation.ts';
import { findErrorMessage } from '@utils/validationUtils.ts';
import InputTimezone from '@components/ringil3/Input/InputTimezone/InputTimezone.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgPlus from '@icons/plus.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  onComplete: () => void;
}

interface FindCarrierForm {
  crn: string;
  state: string;
}

const AddNewCarrier: React.FC<ComponentProps> = ({ visible, onComplete, onHide }) => {
  const { t } = useTranslation('common');
  const { cuCreateCarrier, cuGetCarrierInfoByCrnAndState } = useContext(ApiContext);
  const [newCarrier, setNewCarrier] = useImmer<CarrierSearchOutDTO>(undefined);
  const [foundCarrier, setFoundCarrier] = useState(undefined);
  const [findCarrierForm, setFindCarrierForm] = useImmer<FindCarrierForm>({
    state: null,
    crn: null,
  });
  const [myContacts, setMyContacts] = useImmer<CarrierContactPersonDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      clear();
      clear2();
      setMyContacts(draft => {
        return [];
      });
      setFindCarrierForm(draft => {
        return { state: '', crn: '' };
      });
      setNewCarrier(draft => {
        return undefined;
      });
      setFoundCarrier(undefined);
      setLoading(false);
    }
  }, [visible]);

  const findCarrier = () => {
    cuGetCarrierInfoByCrnAndState(findCarrierForm.crn, findCarrierForm.state, data => {
      if (data) {
        setNewCarrier(draft => {
          setFoundCarrier(true);
          return data;
        });
      } else {
        setNewCarrier(draft => {
          setFoundCarrier(false);
          return {
            name: '',
            crn: findCarrierForm.crn,
            contactPhone: '',
            contactPerson: '',
            city: '',
            carrierId: null,
            zipCode: '',
            streetNr: '',
            contactEmail: '',
            country: findCarrierForm.state,
            tin: '',
            timezoneId: '',
            version: 0,
          };
        });
      }
    });
  };

  const saveCarrier = () => {
    setLoading(true);
    cuCreateCarrier(
      {
        crn: newCarrier.crn,
        city: newCarrier.city,
        name: newCarrier.name,
        country: newCarrier.country,
        zipCode: newCarrier.zipCode,
        tin: newCarrier.tin,
        timezoneId: newCarrier.timezoneId,
        contacts:
          foundCarrier === true
            ? myContacts
            : [
                {
                  contactPerson: newCarrier.contactPerson,
                  contactEmail: newCarrier.contactEmail,
                  contactPhone: newCarrier.contactPhone,
                } as CarrierContactPersonDTO,
                ...myContacts,
              ],
        streetNr: newCarrier.streetNr,
      },
      () => {
        setLoading(false);
        onComplete();
      },
      null,
      {
        onValidationFailed: e => {
          setLoading(false);
          setBeValidationErrors(e);
        },
        onError: (_error, showStandardError) => {
          setLoading(false);
          showStandardError();
        }
      },
    );
  };

  const findValidation = yup.object().shape({
    crn: yup
      .string()
      .required(t('validationCrn.required', 'CRN is required'))
      .test('crn-format', function(value) {
        const { state } = this.parent;
        if (!value || !state) return true;
        const isValid = validateCrn(value, state);
        if (!isValid) {
          const formatDesc = getCrnFormatDescription(state, t);
          return this.createError({
            message: formatDesc
              ? t('validationCrn.crnInvalidFormat', `Invalid CRN format (expected format: {{format}})`, { format: formatDesc })
              : t('validationCrn.crnInvalid', `Invalid CRN format`),
          });
        }
        return true;
      }),
    state: yup.string().required(),
  });

  const { clear, validationErrors, validateAndSend } = useValidation(findValidation, findCarrier);

  const myContactsSchema = yup.object().shape({
    contactPerson: yup.string().required(),
    contactEmail: yup.string().email().required(),
    contactPhone: yup.string().required(),
  });

  const createValidation = yup.object().shape({
    name: yup.string().required(),
    zipCode: yup.string().required(),
    city: yup.string().required(),
    streetNr: yup.string().required(),
    timezoneId: yup.string().required(),
    foundCarrier: yup.boolean(),
    contactPerson: yup.string().when('foundCarrier', {
      is: false,
      then: schema => schema.required(),
      otherwise: schema => schema.notRequired()
    }),
    contactEmail: yup.string().when('foundCarrier', {
      is: false,
      then: schema => schema.email().required(),
      otherwise: schema => schema.notRequired()
    }),
    contactPhone: yup.string().when('foundCarrier', {
      is: false,
      then: schema => schema.required(),
      otherwise: schema => schema.notRequired()
    }),
    myContacts: yup.array().of(myContactsSchema),
  });

  const { clear: clear2, validationErrors: validationErrors2, validateAndSend: validateAndSend2 } = useValidation(createValidation, () => saveCarrier());

  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();

  const handleBack = () => {
    setBeValidationErrors(null);
    onHide();
  };

  return (
    <RingilSidebar
      visible={visible}
      heading={t(`AddNewCarrier.createANewCarriers`, `Vytvořit nového dopravce`)}
      onBack={handleBack}
      onContinue={() => validateAndSend2({ ...newCarrier, myContacts, foundCarrier })}
      continueLabel={t(`AddNewCarrier.add`, `Přidat`)}
      loading={loading}
      showSaveButton={!isNullOrUndefined(foundCarrier) && !!newCarrier}
      maxWidth="750px"
    >
        <BackendValidationComponent />

        <InputLayout>
          <InputWrapper
            label={`${t('AddNewCarrier.crnTest2', 'CRN')} *`}
            type="text"
            value={findCarrierForm.crn}
            onChange={e => {
              setFindCarrierForm(draft => {
                draft.crn = (e.target as HTMLInputElement).value;
              });
            }}
            classNameWrapper="p-col-12 p-md-6"
            error={findErrorMessage(validationErrors, 'crn', t)}
          />
          <InputCountry
            label={`${t(`CreateOrEditPartnerLocation.country`, `Stát`)} *`}
            value={findCarrierForm.state}
            onChange={e => {
              setFindCarrierForm(draft => {
                draft.state = e;
              });
            }}
            classNameWrapper="p-col-12 p-md-6"
            error={findValidationMessage(validationErrors, 'state', t)}
            allowAutoSelect={false}
          />
        </InputLayout>
        <div className="mt-2 mb-2">
          <RingilButton
            mode={'filled-rectangle'}
            label={t(`AddNewCarrier.findCarrier`, `Vyhledat dopravce`)}
            onClick={() => validateAndSend(findCarrierForm)}
          />
        </div>
        {!isNullOrUndefined(foundCarrier) && newCarrier && (
          <>
            <InputLayout>
              <InputWrapper
                label={`${t('AddNewCarrier.carrierName', 'Název dopravce')} *`}
                type="text"
                value={newCarrier.name}
                onChange={e => {
                  setNewCarrier(draft => {
                    draft.name = (e.target as HTMLInputElement).value;
                  });
                }}
                classNameWrapper="p-col-12 p-md-6"
                disabled={newCarrier.carrierId !== null}
                error={findValidationMessage(validationErrors2, 'name', t)}
              />
              <InputWrapper
                label={`${t('AddNewCarrier.tin', 'TIN')}`}
                type="text"
                value={newCarrier.tin}
                onChange={e => {
                  setNewCarrier(draft => {
                    draft.tin = (e.target as HTMLInputElement).value;
                  });
                }}
                classNameWrapper="p-col-12 p-md-6"
                disabled={newCarrier.carrierId !== null}
                error={findValidationMessage(validationErrors2, 'tin', t)}
              />
            </InputLayout>
            <InputLayout>
              <InputWrapper
                label={`${t('AddNewCarrier.zipCode', 'PSČ')} *`}
                type="text"
                value={newCarrier.zipCode}
                onChange={e => {
                  setNewCarrier(draft => {
                    draft.zipCode = (e.target as HTMLInputElement).value;
                  });
                }}
                disabled={newCarrier.carrierId !== null}
                classNameWrapper="p-col-12 p-md-6"
                error={findValidationMessage(validationErrors2, 'zipCode', t)}
              />
              <div className="p-col-12 p-md-6 w-full">
                <InputTimezone
                  label={`${t('AddNewCarrier.timezone', 'Timezone')} *`}
                  value={newCarrier.timezoneId}
                  onChange={v => setNewCarrier(draft => { draft.timezoneId = v; })}
                  disabled={newCarrier.carrierId !== null}
                  classNameWrapper="p-field"
                />
                {findValidationMessage(validationErrors2, 'timezoneId', t) && <div className="p-invalid-feedback">{findValidationMessage(validationErrors2, 'timezoneId', t)}</div>}
              </div>
            </InputLayout>
            <InputLayout>
              <InputWrapper
                label={`${t('AddNewCarrier.city', 'Město')} *`}
                type="text"
                value={newCarrier.city}
                onChange={e => {
                  setNewCarrier(draft => {
                    draft.city = (e.target as HTMLInputElement).value;
                  });
                }}
                disabled={newCarrier.carrierId !== null}
                classNameWrapper="p-col-12 p-md-6"
                error={findValidationMessage(validationErrors2, 'city', t)}
              />
              <InputWrapper
                label={`${t('AddNewCarrier.streetNr', 'Ulice a číslo')} *`}
                type="text"
                value={newCarrier.streetNr}
                onChange={e => {
                  setNewCarrier(draft => {
                    draft.streetNr = (e.target as HTMLInputElement).value;
                  });
                }}
                disabled={newCarrier.carrierId !== null}
                classNameWrapper="p-col-12 p-md-6"
                error={findValidationMessage(validationErrors2, 'streetNr', t)}
              />
            </InputLayout>

            <InputLayout>
              <InputWrapper
                label={`${t('AddNewCarrier.mainContact', 'Kontaktní osoba')} *`}
                type="text"
                value={newCarrier.contactPerson}
                onChange={e =>
                  setNewCarrier(draft => {
                    draft.contactPerson = (e.target as HTMLInputElement).value;
                  })
                }
                disabled={foundCarrier === true}
                error={findValidationMessage(validationErrors2, 'contactPerson', t)}
              />
              <InputWrapper
                label={`${t('AddNewCarrier.mainContactPhone', 'Telefon')} *`}
                type="text"
                value={newCarrier.contactPhone}
                disabled={foundCarrier === true}
                onChange={e =>
                  setNewCarrier(draft => {
                    draft.contactPhone = (e.target as HTMLInputElement).value;
                  })
                }
                error={findValidationMessage(validationErrors2, 'contactPhone', t)}
              />
              <InputWrapper
                label={`${t('AddNewCarrier.mainContactMail', 'Email')} *`}
                type="text"
                value={newCarrier.contactEmail}
                disabled={foundCarrier === true}
                onChange={e =>
                  setNewCarrier(draft => {
                    draft.contactEmail = (e.target as HTMLInputElement).value;
                  })
                }
                error={findValidationMessage(validationErrors2, 'contactEmail', t)}
              />
            </InputLayout>

            {myContacts.map((contact, index) => {
              return (
                <React.Fragment key={index}>
                  <InputLayout>
                    <InputWrapper
                      label={t(`AddNewCarrier.contact`, `contact`)}
                      value={contact.contactPerson}
                      onChange={e =>
                        setMyContacts(draft => {
                          draft[index].contactPerson = (e.target as HTMLInputElement).value;
                        })
                      }
                      type="text"
                      error={findValidationMessage(validationErrors2, `myContacts[${index}].contactPerson`, t)}
                    />
                    <InputWrapper
                      label={t(`AddNewCarrier.contactPhone`, `contact phone`)}
                      type="text"
                      value={contact.contactPhone}
                      onChange={e =>
                        setMyContacts(draft => {
                          draft[index].contactPhone = (e.target as HTMLInputElement).value;
                        })
                      }
                      error={findValidationMessage(validationErrors2, `myContacts[${index}].contactPhone`, t)}
                    />
                    <InputWrapper
                      label={t(`AddNewCarrier.contactEmail`, `contact email`)}
                      type="text"
                      value={contact.contactEmail}
                      onChange={e =>
                        setMyContacts(draft => {
                          draft[index].contactEmail = (e.target as HTMLInputElement).value;
                        })
                      }
                      error={findValidationMessage(validationErrors2, `myContacts[${index}].contactEmail`, t)}
                    />
                  </InputLayout>
                  <div className={'flex justify-end'}>
                    <RingilButton
                      mode={'table'}
                      icon={<SvgDelete />}
                      onClick={() => {
                        setMyContacts(draft => {
                          return draft.filter((t, i) => i !== index);
                        });
                      }}
                    />
                  </div>
                </React.Fragment>
              );
            })}
            {foundCarrier === false && (
              <div className="mt-2">
                <RingilButton
                  mode={'outlined-rectangle'}
                  label={t(`AddNewCarrier.addContact`, `Přidat kontakt`)}
                  icon={<SvgPlus />}
                  onClick={() => {
                    setMyContacts(draft => {
                      draft.push({
                        contactEmail: '',
                        contactPerson: '',
                        contactPhone: '',
                      });
                    });
                  }}
                />
              </div>
            )}
          </>
        )}
    </RingilSidebar>
  );
};

export default AddNewCarrier;
